// import { useRequest } from 'ahooks'
import { Image, Table, TableColumnsType, Tag } from 'antd'
import { useAtom } from 'jotai'
import { uniqBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DepartmentName, UserAvatar, UserName } from '@/components'
import { JiraColumnProps } from '@/modules/project/project-detail/project-jql/project-jql-table-config.component'
import { usersMapAtom } from '@/modules/user'
import { ResourceType } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

export interface JiraIssue {
  key: string
  summary: string
  assignee: string
  reporter: string
  issueType: {
    iconUrl: string
    name: string
  }
  priority: {
    iconUrl: string
    name: string
  }
  status: {
    name: string
    statusCategory: {
      colorName: string
    }
  }
  bugResolved: string
  linkedIssues: JiraIssue[]
  isReleaseType: boolean
  estimate: number
  actualWork: number
  startDate: string
  endDate: string
  linkedIssueKeys: string[]
}

export interface JiraResp {
  selfId: string
  maxResults: number
  total: number
  issues: JiraIssue[]
}

interface JQLTableProps {
  columns: JiraColumnProps[]
  loading: boolean
  issues: JiraIssue[]
  expandable?: boolean
  checkable?: { keys: string[]; setKeys: (selectedRowKeys: React.Key[], selectedRows: JiraIssue[]) => void }
  entity: {
    name: string
    type: ResourceType
  }
}

export const JQLTable: React.FC<JQLTableProps> = ({
  columns: jiraColumns,
  issues,
  loading,
  checkable,
  entity,
  // expandable = true, // 区分一二级 table
}) => {
  const { t, i18n } = useTranslation()
  const isZH = i18n.resolvedLanguage === 'zh'
  // const [linkedIssues, setLinkedIssues] = React.useState<Record<string, JiraIssue[]>>({})

  const [usersMap] = useAtom(usersMapAtom)

  // const {
  //   params,
  //   loading: expandLoading,
  //   run,
  // } = useRequest<JiraResp>(
  //   (jql, key) => ({
  //     url: '/api/project/getJira',
  //     params: {
  //       jql,
  //       key,
  //     },
  //   }),
  //   {
  //     manual: true,
  //     refreshOnWindowFocus: false,
  //     debounceInterval: 800,
  //     onSuccess(data, params) {
  //       setLinkedIssues((prev) => {
  //         return {
  //           ...prev,
  //           [params[1]]: data.issues,
  //         }
  //       })
  //     },
  //   }
  // )

  const originColumns: TableColumnsType<JiraIssue> = [
    {
      key: 'key',
      title: 'Key',
      dataIndex: 'key',
      width: 100,
      render: (_, d) => {
        return (
          <a href={`https://jira.agoralab.co/browse/${d.key}`} target='_blank' rel='noopener noreferrer'>
            {d.key}
          </a>
        )
      },
      filters: uniqBy(
        issues.map((issue) => ({
          text: issue.key.split('-')[0],
          value: issue.key.split('-')[0],
        })),
        'value'
      ),
      onFilter: (value, record) => record.key.toLowerCase().split('-')[0] === (value + '').toLowerCase(),
    },
    {
      key: 'assignee',
      title: t('assignee'),
      width: 100,
      align: 'center',
      render: (_, d) => {
        return <UserAvatar size={20} email={d.assignee} showName />
      },
      filters: uniqBy(
        issues
          .filter((issue) => issue.assignee)
          .map((issue) => ({
            text: <UserName email={issue.assignee} />,
            value: issue.assignee,
          })),
        'value'
      ),
      onFilter: (value, record) => record.assignee === value,
    },
    {
      key: 'reporter',
      title: t('reporter'),
      width: 100,
      align: 'center',
      render: (_, d) => {
        return <UserAvatar size={20} email={d.reporter} showName />
      },
      filters: uniqBy(
        issues
          .filter((issue) => issue.reporter)
          .map((issue) => ({
            text: <UserName email={issue.reporter} />,
            value: issue.reporter,
          })),
        'value'
      ),
      onFilter: (value, record) => record.reporter === value,
    },
    {
      key: 'type',
      title: t('type'),
      width: 80,

      render: (_, d) => {
        return <Image preview={false} width={16} height={16} src={d.issueType.iconUrl} alt={d.issueType.name} />
      },
      filters: uniqBy(
        issues
          .filter((issue) => issue.issueType.name)
          .map((issue) => ({
            text: issue.issueType.name,
            value: issue.issueType.name,
          })),
        'value'
      ),
      onFilter: (value, record) => record.issueType.name === value,
    },
    {
      key: 'summary',
      title: t('summary'),
      width: 250,
      dataIndex: 'summary',
      render: (text) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text}</div>,
    },
    {
      key: 'priority',
      title: t('priority'),
      width: 100,
      align: 'center',
      render: (_, d) => {
        return <Image preview={false} width={16} height={16} src={d.priority.iconUrl} alt={d.priority.name} />
      },
    },
    {
      key: 'status',
      title: t('status'),
      width: 100,
      align: 'center',
      render: (_, d) => {
        const color = d.status.statusCategory.colorName === 'blue-gray' ? 'blue' : d.status.statusCategory.colorName
        return <Tag color={color}>{d.status.name}</Tag>
      },
      filters: uniqBy(
        issues
          .filter((issue) => issue.status.name)
          .map((issue) => ({
            text: issue.status.name,
            value: issue.status.name,
          })),
        'value'
      ),
      onFilter: (value, record) => record.status.name === value,
    },
    {
      key: 'bugResolved',
      title: t('bugResolved'),
      width: 100,
      align: 'center',
      render: (_, d) => <span>{d.bugResolved}</span>,
    },
    {
      key: 'estimateWorkload',
      title: t('estimateWorkload'),
      width: isZH ? 150 : 100,
      align: 'center',
      render: (_, d) => {
        return d.estimate
      },
    },
    {
      key: 'actualWorkload',
      title: t('actualWorkload'),
      width: isZH ? 150 : 100,
      align: 'center',
      render: (_, d) => {
        return d.actualWork
      },
    },
    {
      title: t('startend'),
      width: 200,
      align: 'center',
      render: (_, d) => {
        return `${d.startDate || ''} - ${d.endDate || ''}`
      },
    },
    {
      key: 'department',
      title: t('department'),
      width: 150,
      align: 'center',
      render: (_, d) => {
        const user = usersMap[d.assignee]
        if (!user || !user.departmentIds.length) return null
        return <DepartmentName canRead={true} departmentId={user.departmentIds[0]} />
      },
    },
    {
      key: 'releaseStatus',
      title: t('releaseStatus'),
      width: 100,
      align: 'center',
      // 待补充
      render: () => '',
    },
  ]

  const filteredColumns = originColumns.filter((col) =>
    jiraColumns.some((jiraCol) => jiraCol.key === col.key && jiraCol.isSelected)
  )

  // 增加操作 column
  const columns = filteredColumns.concat([
    {
      key: 'operation',
      title: t('operation'),
      width: 150,
      align: 'center',
      render: (_, d) => {
        if (!d.isReleaseType) return null
        return (
          <a
            href={`${
              window.location.hostname.includes('pm.agoralab.co')
                ? 'https://www.agoralab.co/'
                : 'https://workplace-staging.agoralab.co/'
            }jira-graph/?keyword=${d.key}`}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              const event_category = traceEvent.fullPathClick.event_categories.ef_pmo_project_detail
              ga('event', traceEvent.fullPathClick.events.click_progress_bar, {
                event_category,
                action: traceEvent.fullPathClick.action,
                event_label: entity.name,
                target: d.key,
              })
            }}
          >
            {t('look up jira')}
          </a>
        )
      },
    },
  ])

  return (
    <Table
      size='small'
      loading={loading}
      rowSelection={checkable ? { selectedRowKeys: checkable.keys, onChange: checkable.setKeys } : undefined}
      columns={columns}
      locale={{ emptyText: t('no data') }}
      dataSource={issues}
      pagination={false}
      // expandable={{
      //   expandedRowRender: (record) => {
      //     return (
      //       <JQLTable
      //         issues={linkedIssues[record.key] || []}
      //         columns={jiraColumns}
      //         loading={loading || (params[1] === record.key && expandLoading)}
      //         expandable={false}
      //       />
      //     )
      //   },

      //   onExpand: (expand, record) => {
      //     if (expand) {
      //       run(`issue in linkedIssues(${record.key})`, record.key)
      //     }
      //   },
      //   rowExpandable: (record) => expandable && record.linkedIssueKeys.length > 0,
      // }}
    />
  )
}
